/**
 * <shop-the-look> — turns the parent collection-item card into a trigger that
 * opens this look's popup, and (Flow 1) lets you navigate between sibling looks
 * with arrows + finger-swipe. Open/close/overlay/focus is handled by the global
 * popup singleton (@theme/popup); opening another look's popup auto-closes the
 * current one. ("View all looks" is a plain link, rendered only when a URL is
 * set — no JS wiring here.)
 *
 * Ships with blocks/shop-the-look.liquid (per-block loader). The popup markup is
 * a hidden descendant; the card is its closest .collection-item ancestor. The
 * nav footer is shown only when more than one look exists on the page (so a
 * standalone single look — Figma Flow 3 — has no arrows / view-all).
 *
 * Flow 2 (deep link): a button elsewhere links to /pages/lookbook-X?look=<handle>.
 * On load this page smooth-scrolls to that look's card and auto-opens its popup
 * (after which Flow 1 applies). See the module-level handler at the bottom.
 *
 * Dependencies: @theme/popup, @theme/analytics
 */

import { globalPopup } from '@theme/popup';
import { EVENTS, trackEvent } from '@theme/analytics';

const SWIPE_THRESHOLD = 50; // px — min horizontal travel to count as a look swipe
// A gesture only changes the look when it travels clearly further sideways than it
// does vertically. Without this a vertical scroll of the panel that drifted 50px
// sideways flipped to the next look (869ejuxwn).
const SWIPE_AXIS_RATIO = 1.5;

class ShopTheLook extends HTMLElement {
  #touchX = null;
  #touchY = 0;

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.popup = this.querySelector('.stl-popup');
    if (!this.popup) return;

    this.card = this.closest('.collection-item');

    // Move the dialog OUT of the card FIRST — to <body>, before marking the card a button.
    // Two reasons: (1) a11y — the trigger must not contain the dialog it opens (a focusable
    // role="button" should never wrap a role="dialog" with its own buttons/links); (2) the popup
    // is position:fixed and the card sits inside a Swiper-transformed / animated ancestor, where
    // a transformed ancestor makes position:fixed resolve against it instead of the viewport.
    // <body> fixes both (true viewport overlay + escapes any ancestor stacking context). Keep an
    // owner-card back-ref for Flow 2 (closest('.collection-item') won't resolve after the move).
    // Teleporting to <body> leaves the section's colorGroup ancestor behind, so the popup's scheme
    // vars (--background_color / --primary_text) would resolve against :root (primary). Copy the
    // owning section's colorGroup class onto the popup so it keeps that section's scheme at body
    // level (same idiom as pdp-sticky-atc #inheritColorGroup / the product-gallery scheme sync in siblings).
    const schemeSource = this.closest('[class*="colorGroup--"]');
    const colorGroup = [...(schemeSource?.classList ?? [])].find((cls) =>
      cls.startsWith('colorGroup--')
    );
    if (colorGroup) this.popup.classList.add(colorGroup);

    // The popup leaves its section as well: keep the section type so analytics can report it.
    const sectionType = this.closest('[data-section-type]')?.dataset.sectionType;
    if (sectionType) this.popup.dataset.sectionType = sectionType;

    this.popup._ownerCard = this.card;
    document.body.appendChild(this.popup);
    this.popup.addEventListener('popup:opened', this.#onPopupOpened);

    // Now own the full look-trigger affordance. The server can't detect a look — Shopify renders
    // nested blocks through content_for and masks their type/settings as "@theme" (only the opaque
    // block id is readable, which is not a reliable type signal). And the popup is JS-only anyway,
    // so the card should only become a button once we've wired it (and its dialog is out of it).
    if (this.card) {
      this.card.classList.add('collection-item--has-look');
      this.card.setAttribute('role', 'button');
      this.card.setAttribute('tabindex', '0');
      this.card.setAttribute('aria-haspopup', 'dialog');
      const popupLabel = this.popup.getAttribute('aria-label');
      if (popupLabel) this.card.setAttribute('aria-label', popupLabel);
      this.card.setAttribute('aria-controls', this.popup.id);
      // In look mode the card opens the popup — drop the showcase link if a url was set.
      this.card.querySelector('.collection-item__link')?.remove();
      this.card.addEventListener('click', this.#onCardActivate);
      this.card.addEventListener('keydown', this.#onCardKeydown);
    }

    // Grid looks = those wrapped in a lookbook card (Flow 1), scoped to THE OWNING SECTION.
    // Scope on .shopify-section (Shopify wraps every section in it) so this works for both the
    // Lookbook (.lookbook-looks) and Collection showcase (.collection-showcase) — and so a page
    // that has both (lookbook grid + a showcase below) doesn't merge their looks into one wrong
    // n/total. A document-wide query would count looks from every section on the page.
    // A standalone / button-linked look (no card) is Flow 3 — just the look + products, no
    // arrows / counter / "View all looks".
    const scope = this.closest('.shopify-section') || document;
    this.gridLooks = [...scope.querySelectorAll('shop-the-look')].filter((l) =>
      l.closest('.collection-item')
    );

    // Flow 1 nav — only for a grid look when there's more than one.
    if (this.card && this.gridLooks.length > 1) {
      this.popup.classList.add('stl-popup--multi');

      // Counter "n/total" — nav is rendered twice (top + footer), set both.
      const position = `${this.gridLooks.indexOf(this) + 1}/${this.gridLooks.length}`;
      this.popup.querySelectorAll('[data-stl-counter]').forEach((el) => {
        el.textContent = position;
      });

      this.popup
        .querySelectorAll('[data-stl-prev]')
        .forEach((btn) => btn.addEventListener('click', this.#onPrev));
      this.popup
        .querySelectorAll('[data-stl-next]')
        .forEach((btn) => btn.addEventListener('click', this.#onNext));

      // Finger-swipe between looks — anywhere on the panel EXCEPT the product
      // scroller (a swipe there scrolls products instead of changing the look).
      this.panel = this.popup.querySelector('.stl-popup__panel');
      this.panel?.addEventListener('touchstart', this.#onTouchStart, { passive: true });
      this.panel?.addEventListener('touchend', this.#onTouchEnd, { passive: true });
    }
  }

  disconnectedCallback() {
    this.popup?.removeEventListener('popup:opened', this.#onPopupOpened);
    this.card?.removeEventListener('click', this.#onCardActivate);
    this.card?.removeEventListener('keydown', this.#onCardKeydown);
    this.panel?.removeEventListener('touchstart', this.#onTouchStart);
    this.panel?.removeEventListener('touchend', this.#onTouchEnd);
    // Nav listeners (stable bound fields) — remove explicitly so a Section
    // Rendering re-render that re-uses the DOM doesn't double-bind.
    this.popup
      ?.querySelectorAll('[data-stl-prev]')
      .forEach((btn) => btn.removeEventListener('click', this.#onPrev));
    this.popup
      ?.querySelectorAll('[data-stl-next]')
      .forEach((btn) => btn.removeEventListener('click', this.#onNext));
    // The popup was moved to <body> on connect. Close it through the shared
    // lifecycle first so a Section Rendering re-render cannot orphan its
    // body-scroll lock, then remove the teleported DOM.
    if (this.popup) {
      globalPopup.closePopup(this.popup);
      this.popup.remove();
    }
  }

  #open() {
    this.popup.dataset.openTrigger = 'card';
    // Pass the card as opener so focus returns to it when the popup closes.
    globalPopup.openPopup(this.popup, false, this.card);
  }

  // Every open path ends in globalPopup.openPopup (card, "Look NN" button, arrows, swipe,
  // deep link, any toggle-popup button), so the popup's own event is the one place to count
  // an open without double-firing. Callers that know their cause stamp data-open-trigger
  // first; the rest is derived from the opener element.
  #onPopupOpened = (event) => {
    const trigger = this.popup.dataset.openTrigger || this.#triggerFromOpener(event.detail?.opener);
    delete this.popup.dataset.openTrigger;
    const index = this.gridLooks?.indexOf(this) ?? -1;

    trackEvent(
      EVENTS.shopTheLookOpen,
      {
        look_id: this.popup.id,
        look_block_id: this.dataset.blockId,
        look_name: this.dataset.lookName,
        look_position: index === -1 ? null : index + 1,
        look_total: index === -1 ? null : this.gridLooks.length,
        products_count: Number(this.dataset.productsCount),
        trigger,
      },
      this
    );
  };

  #triggerFromOpener(opener) {
    if (!(opener instanceof Element)) return 'button';
    if (opener === this.card) return 'card';
    if (this.card?.contains(opener)) return 'card_button';
    return 'button';
  }

  // The dialog lives at <body> (moved on connect), so it's never a descendant of the card —
  // a click/keydown here can only originate inside the card. The only inner interactive we
  // yield to is a real link/button (e.g. a nested media link), never the popup.
  #onCardActivate = (event) => {
    if (event.target.closest('a, button')) return;
    event.preventDefault();
    this.#open();
  };

  #onCardKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (event.target.closest('a, button')) return;
    event.preventDefault();
    this.#open();
  };

  // Open the prev/next look (delta -1 / +1), wrapping around. globalPopup closes
  // the current popup when it opens the next one.
  #go(delta, trigger) {
    const all = this.gridLooks || [];
    const index = all.indexOf(this);
    if (index === -1 || all.length < 2) return;
    const target = all[(index + delta + all.length) % all.length];
    if (!target?.popup) return;
    target.popup.dataset.openTrigger = trigger;
    // Opener = the target look's own card, so closing after navigating returns focus there.
    globalPopup.openPopup(target.popup, false, target.card);
  }

  #onPrev = (event) => {
    event.preventDefault();
    this.#go(-1, 'nav_prev');
  };

  #onNext = (event) => {
    event.preventDefault();
    this.#go(1, 'nav_next');
  };

  #onTouchStart = (event) => {
    // Ignore swipes that begin on the horizontally-scrollable product list —
    // those scroll the products; a swipe anywhere else changes the look.
    if (event.target.closest('.stl-popup__products')) {
      this.#touchX = null;
      return;
    }
    this.#touchX = event.changedTouches[0].clientX;
    this.#touchY = event.changedTouches[0].clientY;
  };

  #onTouchEnd = (event) => {
    if (this.#touchX == null) return;
    const dx = event.changedTouches[0].clientX - this.#touchX;
    const dy = event.changedTouches[0].clientY - this.#touchY;
    this.#touchX = null;
    if (Math.abs(dx) < SWIPE_THRESHOLD) return;
    if (Math.abs(dx) < Math.abs(dy) * SWIPE_AXIS_RATIO) return;
    this.#go(dx < 0 ? 1 : -1, 'swipe'); // swipe left → next, right → prev
  };
}

if (!customElements.get('shop-the-look')) {
  customElements.define('shop-the-look', ShopTheLook);
}

// Flow 2 deep link — runs once on page load (deferred module; the looks are in the
// DOM and upgraded by now). A button elsewhere links to ?look=<handle> where the
// handle is a look's Popup handle (= the popup element id). We smooth-scroll to the
// look's card, then auto-open its popup once the scroll settles. From there Flow 1
// (arrows / View all looks) applies.
(() => {
  const handle = new URL(window.location.href).searchParams.get('look');
  if (!handle) return;

  const popup = document.getElementById(handle);
  if (!popup || !popup.classList.contains('stl-popup')) return;

  // Strip ?look= so a refresh / shared URL doesn't keep re-triggering.
  const cleanUrl = new URL(window.location.href);
  cleanUrl.searchParams.delete('look');
  history.replaceState(history.state, '', cleanUrl);

  // Popup was moved to <body> on connect, so closest('.collection-item') no longer works;
  // use the back-reference set in connectedCallback (fallback for a not-yet-upgraded look).
  const card = popup._ownerCard || popup.closest('.collection-item');
  // Opener = the look's card so closing the deep-linked popup returns focus to the grid.
  const open = () => {
    // A click on the card before scrollend already opened it; a second openPopup would
    // close and reopen the same popup (and count a second open).
    if (popup.getAttribute('aria-hidden') === 'false') return;
    popup.dataset.openTrigger = 'deep_link';
    globalPopup.openPopup(popup, false, card);
  };
  if (!card) {
    open();
    return;
  }

  card.scrollIntoView({ behavior: 'smooth', block: 'center' });

  let opened = false;
  // Any popup the visitor opens before the auto-open fulfils the deep link; re-opening the
  // linked look would close their choice and count a second open.
  document.addEventListener(
    'popup:opened',
    () => {
      opened = true;
    },
    { once: true }
  );
  const openOnce = () => {
    if (opened) return;
    opened = true;
    open();
  };

  if ('onscrollend' in window) {
    window.addEventListener('scrollend', openOnce, { once: true });
    // timeout: fallback when scrollend never fires (card already in view / reduced motion)
    setTimeout(openOnce, 1500);
  } else {
    // timeout: approximate smooth-scroll duration before auto-opening (no scrollend support)
    setTimeout(openOnce, 700);
  }
})();
